"use client";

import { Popover as BasePopover } from "@base-ui/react/popover";
import { cn } from "@shared/lib/cn";
import * as React from "react";
import { renderAsChild } from "./slot";

function Popover({ ...props }: React.ComponentProps<typeof BasePopover.Root>) {
  return <BasePopover.Root data-slot="popover" {...props} />;
}

type TriggerProps = React.ComponentProps<typeof BasePopover.Trigger>;
type RenderProp = NonNullable<TriggerProps["render"]>;

function PopoverTrigger({
  asChild,
  children,
  ...props
}: React.ComponentProps<typeof BasePopover.Trigger> & { asChild?: boolean }) {
  return (
    <BasePopover.Trigger
      data-slot="popover-trigger"
      {...props}
      render={asChild ? (renderAsChild(children) as unknown as RenderProp) : (props.render as RenderProp | undefined)}
    >
      {asChild ? null : children}
    </BasePopover.Trigger>
  );
}

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof BasePopover.Popup> & {
  align?: "start" | "center" | "end";
  sideOffset?: number;
}) {
  // Same stacking fix as TooltipContent / SelectContent for dialogs.
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);
  React.useEffect(() => {
    setPortalContainer(document.body);
  }, []);

  return (
    <BasePopover.Portal container={portalContainer}>
      <BasePopover.Positioner align={align} sideOffset={sideOffset} className="z-100">
        <BasePopover.Popup
          data-slot="popover-content"
          className={cn(
            "bg-popover text-popover-foreground w-72 origin-(--transform-origin) rounded-md border p-4 shadow-md outline-hidden",
            "transition-[transform,opacity] duration-150 data-starting-style:opacity-0 data-starting-style:scale-95 data-ending-style:opacity-0 data-ending-style:scale-95",
            className,
          )}
          {...props}
        />
      </BasePopover.Positioner>
    </BasePopover.Portal>
  );
}

export { Popover, PopoverContent, PopoverTrigger };
